import { useState, useEffect } from "react";
import { PhoneInput } from "react-international-phone";
import PropTypes from "prop-types";
import * as Sentry from "@sentry/react";
import { useThemeContext } from "../../context/themeContext";
import { useUserContext } from "../../context/userContext";
import { getCountryForNumber } from "../../utils/phoneCountry";
import ErrorIcon from "/public/images/icons/inputError.svg?react";
import 'react-international-phone/style.css';
import fetchWithRefresh from "../../utils/fetchWithRefresh";
import { toast } from "react-toastify";
import { LogErrors } from "../../utils/LogErrors";

export default function AdvancedPhoneInput({
    label,
    inputName,
    onClick,
    readOnly,
    defaultValue,
    required,
    error,
    setError,
    onChange,
}) {
    const { theme } = useThemeContext();
    const [activeCountry, setActiveCountry] = useState(null);
    const [allowedCountries, setAllowedCountries] = useState([]);
    const inputError = error?.find(err => err.name === inputName);
    const { user, logout, updateTokens } = useUserContext();

    const handleChange = (value) => {
        if (onChange) onChange(value, inputName);

        // Clear the error for this input field when the value changes
        if (setError && inputError) {
            setError(prevErrors => prevErrors.filter(err => err.name !== inputName));
        }
    };

    const detectCountryFromPhone = (phone) => {
        const detectedCountry = getCountryForNumber(phone);
        setActiveCountry(detectedCountry);
    };

    useEffect(() => {
        if (allowedCountries.length === 0) {
            fetchCountries();
        }
    }, [allowedCountries]);

    useEffect(() => {
        if (defaultValue) {
            detectCountryFromPhone(defaultValue);
        }
    }, [defaultValue]);

    const fetchCountries = async () => {
        await Sentry.startSpan(
            {
                name: "Fetch Active Countries",
                op: "http.request",
            },
            async (span) => {
                try {
                    const response = await fetchWithRefresh(
                        import.meta.env.VITE_API_URL + `Settings/FetchActiveCountries`,
                        {
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${user?.token}`,
                            },
                        },
                        logout,
                        updateTokens,
                        (message) => { toast.info(message); }
                    );

                    // Set HTTP status as an attribute in the span
                    span.setAttribute("http.status_code", response.status);

                    if (response.ok) {
                        const countries = await response.json();
                        setAllowedCountries(countries);
                    } else {
                        span.setStatus("error"); // Mark the span as an error
                        LogErrors(user, logout, updateTokens, "Failed to fetch countries");
                    }
                } catch (error) {
                    span.setStatus("error");
                    Sentry.captureException(error); // Capture error in Sentry
                    LogErrors(user, logout, updateTokens, "An error occurred while fetching countries: " + error);
                } finally {
                    span.end(); // Explicitly end the span
                }
            }
        );
    };

    return (
        <div>
            <label className="flex flex-col gap-1">
                <div>
                    <span className="text-sportsBlack font-medium text-base leading-[26px mb-1]">
                        {label}
                        {required && "*"}
                    </span>
                </div>

                <div className="relative block">
                    <PhoneInput 
                        name={inputName}
                        defaultCountry={activeCountry?.iso2 || "gb"}
                        forceDialCode={true}
                        preferredCountries={["gb"]}
                        countries={allowedCountries.length > 0 ? allowedCountries : [["United Kingdom","gb","44"]]}
                        onChange={(value) => handleChange(value)}
                        inputClassName={`
                            outline-2 -outline-offset-2 w-auto flex-grow placeholder:!text-[#727272] autofill:!bg-sportsLightGrey !bg-sportsLightGrey !border-solid !border-[1px] !border-[#E3E3E3] !h-auto rounded-[4px] !p-3 !text-base !leading-[26px] !text-[#101820] focus:z-10
                            ${theme === "staff" 
                                ? "focus:!outline-sportsBlue focus:outline"
                                : "focus:!outline-sportsYellow focus:outline"
                            }
                            ${inputError && "!pr-10 outline-sportsRed outline z-10"}
                        `} 
                        countrySelectorStyleProps={{
                            buttonClassName: `
                                h-full p-3 !bg-sportsLightGrey [&.react-international-phone-country-selector-button--active]:outline-2 [&.react-international-phone-country-selector-button--active]:outline [&.react-international-phone-country-selector-button--active]:-outline-offset-2
                                ${theme === "staff" 
                                    ? "[&.react-international-phone-country-selector-button--active]:outline-sportsBlue"
                                    : "[&.react-international-phone-country-selector-button--active]:outline-sportsYellow"
                                }
                            `,
                            flagClassName: `ml-0 mr-[10px] w-5`,
                            dropdownArrowClassName: `m-0 !border-none w-6 h-6 bg-center bg-[url('https://stsportsguardianprod001.blob.core.windows.net/sharedassets/icons/chevronDown.svg')] bg-cover bg-no-repeat`,
                            dropdownStyleProps: {
                                className: "p-0 mt-3 rounded shadow-[0_4px_6px_0_rgba(0,0,0,0.05)] border-solid border-[#E3E3E3] border",
                                listItemClassName: "py-[14px] pr-[10px] pl-[16px]",
                                listItemFlagClassName: "w-[19.6px]",
                                listItemCountryNameClassName: "text-base leading-[26px] font-poppins text-[#101820] mr-[4px]",
                                listItemDialCodeClassName: "text-base leading-[26px] font-poppins text-[#101820]",
                            },
                        }}
                        inputProps={{
                            onClick: onClick ? onClick : undefined,
                            readOnly: readOnly,
                            required: required,
                        }}
                        value={defaultValue}
                    />
                    {inputError && (
                        <span className="absolute top-1/2 -translate-y-1/2 right-4 cursor-pointer z-20">
                            <ErrorIcon className="w-6 h-6" />
                        </span>
                    )}
                </div>

                {inputError && (
                    <p className="text-sportsRed text-base leading-[24px]">{inputError?.error}</p>
                )}
            </label>
        </div>
    );
}

AdvancedPhoneInput.propTypes = {
    label: PropTypes.string,
    inputName: PropTypes.string.isRequired,
    onClick: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.bool,
    ]),
    readOnly: PropTypes.bool,
    defaultValue: PropTypes.string,
    required: PropTypes.bool,
    error: PropTypes.array,
    onChange: PropTypes.func,
};